import React from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Facebook, MapPin, Phone, Mail } from 'lucide-react';
import { useSiteSettings } from '../contexts/SiteSettingsContext';

const Footer = () => {
  const settings = useSiteSettings();

  const email = settings?.contact_email || '';
  const phone = settings?.contact_phone || '';
  const address = settings?.address || '';
  const instagram = settings?.social_media?.instagram || '';
  const facebook = settings?.social_media?.facebook || '';

  return (
    <footer className="bg-[#1a1918] text-white" data-testid="site-footer">
      <div className="container mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div>
            <Link to="/" className="flex items-center gap-3 no-underline inline-block mb-4">
              <img src="/logo-circle.jpeg" alt="Tropiluxe" className="w-10 h-10 rounded-full object-cover" />
              <span className="text-2xl font-bold text-white">Tropiluxe</span>
            </Link>
            <p className="text-white/60 leading-relaxed">
              Creating unforgettable luxury dining experiences on Jamaica's most beautiful beaches.
            </p>
            <div className="flex gap-4 mt-6">
              {instagram && (
                <a
                  href={`https://instagram.com/${instagram.replace('@', '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white/70 transition-all duration-200 hover:bg-white/20 hover:text-white"
                  aria-label="Instagram"
                  data-testid="footer-instagram"
                >
                  <Instagram size={18} />
                </a>
              )}
              {facebook && (
                <a
                  href={`https://facebook.com/${facebook}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white/70 transition-all duration-200 hover:bg-white/20 hover:text-white"
                  aria-label="Facebook"
                  data-testid="footer-facebook"
                >
                  <Facebook size={18} />
                </a>
              )}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-bold mb-6">Quick Links</h4>
            <ul className="list-none m-0 p-0 flex flex-col gap-3">
              {['Home', 'Packages', 'Gallery', 'About', 'Contact'].map((link) => (
                <li key={link}>
                  <Link
                    to={`/${link === 'Home' ? '' : link.toLowerCase()}`}
                    className="text-white/60 no-underline transition-colors duration-200 hover:text-white"
                  >
                    {link}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Our Packages */}
          <div>
            <h4 className="text-lg font-bold mb-6">Our Packages</h4>
            <ul className="list-none m-0 p-0 flex flex-col gap-3">
              <li className="text-white/60">Romantic Escape</li>
              <li className="text-white/60">Birthday Bash</li>
              <li className="text-white/60">Family Celebration</li>
              <li className="text-white/60">Themed Event</li>
            </ul>
          </div>

          {/* Contact Us */}
          <div>
            <h4 className="text-lg font-bold mb-6">Contact Us</h4>
            <ul className="list-none m-0 p-0 flex flex-col gap-4">
              {address && (
                <li className="flex items-center gap-3 text-white/60">
                  <MapPin size={16} className="text-[#5f9dff] flex-shrink-0" />
                  <span>{address}</span>
                </li>
              )}
              {phone && (
                <li className="flex items-center gap-3 text-white/60">
                  <Phone size={16} className="text-[#5f9dff] flex-shrink-0" />
                  <a href={`tel:${phone}`} className="text-white/60 no-underline hover:text-white transition-colors">
                    {phone}
                  </a>
                </li>
              )}
              {email && (
                <li className="flex items-center gap-3 text-white/60">
                  <Mail size={16} className="text-[#5f9dff] flex-shrink-0" />
                  <a href={`mailto:${email}`} className="text-white/60 no-underline hover:text-white transition-colors">
                    {email}
                  </a>
                </li>
              )}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/40 text-sm">
            &copy; {new Date().getFullYear()} Tropiluxe. All rights reserved.
          </p>
          <div className="flex gap-6">
            <span className="text-white/40 text-sm cursor-pointer hover:text-white/60 transition-colors">Privacy Policy</span>
            <span className="text-white/40 text-sm cursor-pointer hover:text-white/60 transition-colors">Terms of Service</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
